import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { IconPlus, IconSearch, IconEdit, IconTrash } from './Icons';
import './Payments.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const paymentsSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

const CURRENCY = 'OMR';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const PAYMENT_CATEGORIES = ['Salary', 'Allowance'];

function calcChartTotal(salary, allowance, pasiPercent) {
  const s = Number(salary) || 0;
  const a = Number(allowance) || 0;
  const p = Number(pasiPercent) || 0;
  return (s + a) * (1 - p / 100);
}

function getPeriodKey(periodType, year, month) {
  if (periodType === 'month') return `${year}-${String(month + 1).padStart(2, '0')}`;
  if (periodType === 'quarter') return `${year}-Q${Math.floor(month / 3) + 1}`;
  return `${year}`;
}

function getPeriodLabel(periodType, year, month) {
  if (periodType === 'month') return `${MONTHS[month]} ${year}`;
  if (periodType === 'quarter') return `Q${Math.floor(month / 3) + 1} ${year}`;
  return `${year}`;
}

function getDateRange(periodType, year, month) {
  let start, end;
  if (periodType === 'month') {
    start = new Date(year, month, 1);
    end = new Date(year, month + 1, 0);
  } else if (periodType === 'quarter') {
    const q = Math.floor(month / 3);
    start = new Date(year, q * 3, 1);
    end = new Date(year, q * 3 + 3, 0);
  } else {
    start = new Date(year, 0, 1);
    end = new Date(year, 11, 31);
  }
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

export default function Payments() {
  const now = new Date();
  const [periodType, setPeriodType] = useState('month');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [payments, setPayments] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [salaryChart, setSalaryChart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentsError, setPaymentsError] = useState(null);
  const [salaryChartError, setSalaryChartError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(null);

  const [addForm, setAddForm] = useState({
    expense_date: now.toISOString().slice(0, 10),
    title: '',
    description: '',
    amount: '',
    member_id: '',
  });

  const [editForm, setEditForm] = useState({
    expense_date: '',
    title: '',
    description: '',
    amount: '',
    member_id: '',
  });

  const periodKey = getPeriodKey(periodType, year, month);
  const { start: dateStart, end: dateEnd } = getDateRange(periodType, year, month);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      paymentsSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchPayments = useCallback(async () => {
    setPaymentsError(null);
    const { data, error } = await paymentsSupabase
      .from('expenses')
      .select('id, expense_date, category, title, description, member_id, amount, tax, second_tax, paid, paid_at')
      .in('category', PAYMENT_CATEGORIES)
      .gte('expense_date', dateStart)
      .lte('expense_date', dateEnd)
      .order('expense_date', { ascending: false });
    if (error) {
      setPaymentsError(error.message);
      setPayments([]);
    } else {
      setPayments(data ?? []);
    }
  }, [dateStart, dateEnd]);

  const fetchProfiles = useCallback(async () => {
    const { data, error } = await paymentsSupabase
      .from('profiles')
      .select('id, full_name')
      .order('full_name', { ascending: true, nullsFirst: false });
    if (!error) setProfiles(data ?? []);
  }, []);

  const fetchSalaryChart = useCallback(async () => {
    setSalaryChartError(null);
    const { data, error } = await paymentsSupabase
      .from('salary_chart')
      .select('employee_id, salary, allowance, pasi_percent, total')
      .order('created_at', { ascending: false });
    if (error) {
      setSalaryChartError(error.message);
      setSalaryChart([]);
    } else {
      setSalaryChart(data ?? []);
    }
  }, []);

  const refreshSessionAndFetch = useCallback(async () => {
    setLoading(true);
    setPaymentsError(null);
    setSalaryChartError(null);
    try {
      await paymentsSupabase.auth.getSession();
      await runWakeUp();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      await Promise.race([
        Promise.all([fetchPayments(), fetchProfiles(), fetchSalaryChart()]),
        timeoutPromise,
      ]);
    } catch (e) {
      setPaymentsError(e?.message || 'Failed to load');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [runWakeUp, fetchPayments, fetchProfiles, fetchSalaryChart]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { error: sessionErr } = await paymentsSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setPaymentsError(sessionErr.message);
        setLoading(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
        );
        await Promise.race([
          Promise.all([fetchPayments(), fetchProfiles(), fetchSalaryChart()]),
          timeoutPromise,
        ]);
      } catch (e) {
        if (!cancelled) {
          setPaymentsError(e?.message || 'Failed to load');
          setPayments([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [runWakeUp, fetchPayments, fetchProfiles, fetchSalaryChart]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const chartByMember = React.useMemo(() => {
    const map = {};
    salaryChart.forEach((row) => {
      if (!row.employee_id) return;
      const total = row.total != null ? Number(row.total) : calcChartTotal(row.salary, row.allowance, row.pasi_percent);
      map[row.employee_id] = {
        salary: Number(row.salary) || 0,
        allowance: Number(row.allowance) || 0,
        total,
      };
    });
    return map;
  }, [salaryChart]);

  useEffect(() => {
    if (!showAddModal) return;
    const mid = addForm.member_id || null;
    if (!mid || !chartByMember[mid]) return;
    const chart = chartByMember[mid];
    setAddForm((f) => ({ ...f, amount: chart.allowance ? String(chart.allowance) : '' }));
  }, [showAddModal, addForm.member_id, chartByMember]);

  const monthLabel = getPeriodLabel('month', year, month);

  const displayRows = React.useMemo(() => {
    const salaryExpenses = payments.filter((p) => p.category === 'Salary');
    const allowanceExpenses = payments.filter((p) => p.category === 'Allowance');
    const rows = [];

    if (periodType === 'month') {
      const chartByEmployee = {};
      salaryChart.forEach((row) => {
        if (row.employee_id && !chartByEmployee[row.employee_id]) {
          const total = row.total != null ? Number(row.total) : calcChartTotal(row.salary, row.allowance, row.pasi_percent);
          chartByEmployee[row.employee_id] = total;
        }
      });
      Object.entries(chartByEmployee).forEach(([employeeId, amount]) => {
        const existing = salaryExpenses.find((e) => e.member_id === employeeId);
        if (existing) {
          rows.push({ ...existing, isVirtual: false });
        } else {
          rows.push({
            id: null,
            expense_date: dateStart,
            category: 'Salary',
            title: `${monthLabel} salary`,
            description: null,
            member_id: employeeId,
            amount,
            tax: 0,
            second_tax: 0,
            paid: false,
            paid_at: null,
            isVirtual: true,
          });
        }
      });
    } else {
      salaryExpenses.forEach((e) => rows.push({ ...e, isVirtual: false }));
    }

    allowanceExpenses.forEach((e) => rows.push({ ...e, isVirtual: false }));
    rows.sort((a, b) => {
      const typeOrder = (r) => (r.category === 'Salary' ? 0 : 1);
      if (typeOrder(a) !== typeOrder(b)) return typeOrder(a) - typeOrder(b);
      return (b.expense_date || '').localeCompare(a.expense_date || '');
    });
    return rows;
  }, [periodType, dateStart, monthLabel, salaryChart, payments]);

  const filteredDisplayRows = search.trim()
    ? displayRows.filter(
        (e) =>
          (e.title || '').toLowerCase().includes(search.toLowerCase()) ||
          (e.category || '').toLowerCase().includes(search.toLowerCase()) ||
          (e.description || '').toLowerCase().includes(search.toLowerCase()) ||
          (profiles.find((p) => p.id === e.member_id)?.full_name || '').toLowerCase().includes(search.toLowerCase())
      )
    : displayRows;

  const insights = React.useMemo(() => {
    const rows = filteredDisplayRows;
    const sum = (arr) => arr.reduce((acc, r) => acc + (Number(r.amount) || 0) + (Number(r.tax) || 0) + (Number(r.second_tax) || 0), 0);

    const salaryRows = rows.filter((r) => r.category === 'Salary');
    const allowanceRows = rows.filter((r) => r.category === 'Allowance');
    const paidRows = rows.filter((r) => !!r.paid);
    const unpaidRows = rows.filter((r) => !r.paid);

    const byMember = new Map();
    rows.forEach((r) => {
      const id = r.member_id || '—';
      const prev = byMember.get(id) || 0;
      byMember.set(id, prev + (Number(r.amount) || 0) + (Number(r.tax) || 0) + (Number(r.second_tax) || 0));
    });
    const topMembers = Array.from(byMember.entries())
      .filter(([id]) => id !== '—')
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([id, total]) => ({
        id,
        name: profiles.find((p) => p.id === id)?.full_name || 'Unnamed',
        total,
      }));

    return {
      salaryTotal: sum(salaryRows),
      allowanceTotal: sum(allowanceRows),
      paidTotal: sum(paidRows),
      unpaidTotal: sum(unpaidRows),
      count: rows.length,
      paidCount: paidRows.length,
      unpaidCount: unpaidRows.length,
      allowanceCount: allowanceRows.length,
      salaryCount: salaryRows.length,
      unpaidAllowanceCount: allowanceRows.filter((r) => !r.paid).length,
      topMembers,
    };
  }, [filteredDisplayRows, profiles]);

  const fmtMoney = (n) => `${CURRENCY} ${Number(n || 0).toFixed(2)}`;

  const handleExportExcel = () => {
    const header = ['Date', 'Category', 'Member', 'Title', 'Description', 'Amount', 'Paid'];
    const rows = filteredDisplayRows.map((row) => [
      row.expense_date ?? '',
      row.category ?? '',
      profiles.find((p) => p.id === row.member_id)?.full_name ?? '',
      row.title ?? '',
      row.description ?? '',
      Number(row.amount) || 0,
      row.paid ? 'Yes' : 'No',
    ]);
    const csvLines = [header, ...rows].map((cols) =>
      cols
        .map((v) => {
          const s = v == null ? '' : String(v);
          return s.includes('"') || s.includes(',') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','),
    );
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'payments-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    const amount = Number(addForm.amount) || 0;
    setStatus(null);
    try {
      await runWakeUp();
      const { error } = await paymentsSupabase.from('expenses').insert({
        expense_date: addForm.expense_date || dateStart,
        category: 'Allowance',
        title: addForm.title?.trim() || 'Allowance',
        description: addForm.description?.trim() || null,
        member_id: addForm.member_id || null,
        amount,
        tax: 0,
        second_tax: 0,
        paid: false,
      });
      if (error) throw error;
      setShowAddModal(false);
      setAddForm({
        expense_date: new Date().toISOString().slice(0, 10),
        title: '',
        description: '',
        amount: '',
        member_id: '',
      });
      await fetchPayments();
      setStatus('Allowance added.');
    } catch (err) {
      setStatus(err?.message || 'Failed to add allowance.');
    }
  };

  const openEditModal = (row) => {
    if (!row.id || row.category !== 'Allowance') return;
    setEditingId(row.id);
    setEditForm({
      expense_date: row.expense_date || '',
      title: row.title || '',
      description: row.description || '',
      amount: String(row.amount ?? ''),
      member_id: row.member_id || '',
    });
    setStatus(null);
    setShowEditModal(true);
  };

  const handleEditPayment = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    setStatus(null);
    try {
      await runWakeUp();
      const { error } = await paymentsSupabase
        .from('expenses')
        .update({
          expense_date: editForm.expense_date || null,
          category: 'Allowance',
          title: editForm.title?.trim() || 'Allowance',
          description: editForm.description?.trim() || null,
          member_id: editForm.member_id || null,
          amount: Number(editForm.amount) || 0,
          tax: 0,
          second_tax: 0,
        })
        .eq('id', editingId);
      if (error) throw error;
      setShowEditModal(false);
      setEditingId(null);
      await fetchPayments();
      setStatus('Allowance updated.');
    } catch (err) {
      setStatus(err?.message || 'Failed to update allowance.');
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingId(null);
  };

  const handlePaidToggle = async (row) => {
    const nextPaid = !row.paid;
    try {
      await runWakeUp();
      if (row.isVirtual && row.category === 'Salary' && nextPaid) {
        const { error } = await paymentsSupabase.from('expenses').insert({
          expense_date: row.expense_date || dateStart,
          category: 'Salary',
          title: row.title || `${monthLabel} salary`,
          description: null,
          member_id: row.member_id || null,
          amount: Number(row.amount) || 0,
          tax: 0,
          second_tax: 0,
          paid: true,
          paid_at: new Date().toISOString(),
        });
        if (error) throw error;
      } else if (row.id) {
        const { error } = await paymentsSupabase
          .from('expenses')
          .update({ paid: nextPaid, paid_at: nextPaid ? new Date().toISOString() : null })
          .eq('id', row.id);
        if (error) throw error;
      }
      await fetchPayments();
    } catch (e) {
      setStatus(e?.message || 'Failed to update.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await runWakeUp();
      const { error } = await paymentsSupabase.from('expenses').delete().eq('id', id);
      if (error) throw error;
      await fetchPayments();
      setStatus('Payment deleted.');
    } catch (e) {
      setStatus(e?.message || 'Failed to delete.');
    }
  };

  const handleMarkAllPaid = async () => {
    const unpaid = filteredDisplayRows.filter((r) => !r.paid);
    if (unpaid.length === 0) {
      setStatus('No unpaid payments in this period.');
      return;
    }
    setStatus(null);
    const now = new Date().toISOString();
    try {
      await runWakeUp();
      const virtualUnpaid = unpaid.filter((r) => r.isVirtual && r.category === 'Salary');
      for (const row of virtualUnpaid) {
        const { error: insertErr } = await paymentsSupabase.from('expenses').insert({
          expense_date: row.expense_date || dateStart,
          category: 'Salary',
          title: row.title || `${monthLabel} salary`,
          description: null,
          member_id: row.member_id || null,
          amount: Number(row.amount) || 0,
          tax: 0,
          second_tax: 0,
          paid: true,
          paid_at: now,
        });
        if (insertErr) throw insertErr;
      }
      const idsToUpdate = unpaid.filter((r) => r.id).map((r) => r.id);
      if (idsToUpdate.length > 0) {
        const { error } = await paymentsSupabase
          .from('expenses')
          .update({ paid: true, paid_at: now })
          .in('id', idsToUpdate);
        if (error) throw error;
      }
      await fetchPayments();
      setStatus(`Marked ${unpaid.length} payment(s) as paid.`);
    } catch (e) {
      setStatus(e?.message || 'Failed to mark all as paid.');
    }
  };

  const prevPeriod = () => {
    if (periodType === 'month') {
      if (month === 0) {
        setMonth(11);
        setYear((y) => y - 1);
      } else setMonth((m) => m - 1);
    } else if (periodType === 'quarter') {
      if (month <= 2) {
        setMonth(9);
        setYear((y) => y - 1);
      } else setMonth((m) => m - 3);
    } else {
      setYear((y) => y - 1);
    }
  };

  const nextPeriod = () => {
    if (periodType === 'month') {
      if (month === 11) {
        setMonth(0);
        setYear((y) => y + 1);
      } else setMonth((m) => m + 1);
    } else if (periodType === 'quarter') {
      if (month >= 9) {
        setMonth(0);
        setYear((y) => y + 1);
      } else setMonth((m) => m + 3);
    } else {
      setYear((y) => y + 1);
    }
  };

  const openAddModal = () => {
    setAddForm({
      expense_date: new Date().toISOString().slice(0, 10),
      title: '',
      description: '',
      amount: '',
      member_id: '',
    });
    setStatus(null);
    setShowAddModal(true);
  };

  return (
    <div className="payments-page">
      <div className="payments-card">
        <div className="payments-header-row">
          <h1 className="payments-title">Salary & allowance</h1>
          <div className="payments-header-actions">
            <Link to="/expenses" className="payments-budget-link">
              View full budget in Expenses
            </Link>
            <button
              type="button"
              className="payments-header-btn"
              onClick={handleMarkAllPaid}
              disabled={filteredDisplayRows.filter((r) => !r.paid).length === 0}
              title="Mark all unpaid in this period as paid"
            >
              Tick all
            </button>
            <button
              type="button"
              className="payments-header-btn payments-header-btn-primary"
              onClick={openAddModal}
            >
              <IconPlus />
              Add allowance
            </button>
          </div>
        </div>

        <div className="payments-toolbar">
          <button type="button" className="payments-toolbar-btn" onClick={handleExportExcel} disabled={paymentsError || filteredDisplayRows.length === 0}>
            Excel
          </button>
          <div className="payments-period-wrap">
            <label className="payments-period-label">Period</label>
            <select
              className="payments-period-select"
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              aria-label="Period type"
            >
              <option value="month">Monthly</option>
              <option value="quarter">Quarterly</option>
              <option value="year">Yearly</option>
            </select>
          </div>
          <div className="payments-date-nav">
            <button type="button" className="payments-date-btn" onClick={prevPeriod} aria-label="Previous">‹</button>
            <span className="payments-date-label">{getPeriodLabel(periodType, year, month)}</span>
            <button type="button" className="payments-date-btn" onClick={nextPeriod} aria-label="Next">›</button>
          </div>
          <div className="payments-search-wrap">
            <IconSearch />
            <input
              type="search"
              className="payments-search"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="payments-insights">
          <div className="payments-insights-grid">
            <div className="payments-insight-card">
              <div className="payments-insight-label">Total paid</div>
              <div className="payments-insight-value">{fmtMoney(insights.paidTotal)}</div>
              <div className="payments-insight-sub">{insights.paidCount} paid</div>
            </div>
            <div className="payments-insight-card">
              <div className="payments-insight-label">Total unpaid</div>
              <div className="payments-insight-value">{fmtMoney(insights.unpaidTotal)}</div>
              <div className="payments-insight-sub">{insights.unpaidCount} unpaid</div>
            </div>
            <div className="payments-insight-card">
              <div className="payments-insight-label">Salary</div>
              <div className="payments-insight-value">{fmtMoney(insights.salaryTotal)}</div>
              <div className="payments-insight-sub">{insights.salaryCount} rows</div>
            </div>
            <div className="payments-insight-card">
              <div className="payments-insight-label">Allowance</div>
              <div className="payments-insight-value">{fmtMoney(insights.allowanceTotal)}</div>
              <div className="payments-insight-sub">{insights.allowanceCount} rows</div>
            </div>
          </div>

          {insights.topMembers.length > 0 && (
            <div className="payments-top-members">
              <div className="payments-top-members-title">Top members (by total)</div>
              <div className="payments-top-members-list">
                {insights.topMembers.map((m) => (
                  <div key={m.id} className="payments-top-member">
                    <span className="payments-top-member-name">{m.name}</span>
                    <span className="payments-top-member-total">{fmtMoney(m.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {paymentsError && (
          <p className="payments-error" role="alert">
            Could not load payments: {paymentsError}
          </p>
        )}
        {salaryChartError && (
          <p className="payments-error" role="alert">
            Could not load salary chart: {salaryChartError}. If you use the finance role, run migration{' '}
            <code>030_salary_chart_finance_select.sql</code> in Supabase.
          </p>
        )}
        {status && <p className="payments-status-msg">{status}</p>}

        {loading ? (
          <p className="payments-muted">Loading…</p>
        ) : (
          <div className="payments-table-wrapper">
            <table className="payments-table">
              <thead>
                <tr>
                  <th className="payments-th-paid">Paid</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Member</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th className="payments-th-num">Amount</th>
                  <th className="payments-th-num">VAT</th>
                  <th className="payments-th-num">Total</th>
                  <th className="payments-th-actions" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filteredDisplayRows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="payments-table-empty">
                      {periodType === 'month' && salaryChart.length === 0 && !salaryChartError
                        ? 'Add employees in HR → Salary Chart to see salaries here.'
                        : 'No salary or allowance in this period.'}
                      {false && periodType === 'month' && salaryChart.length === 0 && !salaryChartError && (
                        <span className="payments-empty-msg"> Use “Generate salaries for this month” Add employees in HR → Salary Chart to see salaries here.</span>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredDisplayRows.map((row) => {
                    const rowTotal = (Number(row.amount) || 0) + (Number(row.tax) || 0) + (Number(row.second_tax) || 0);
                    const rowKey = row.id || `salary-${row.member_id}`;
                    const isAllowance = row.category === 'Allowance';
                    return (
                      <tr key={rowKey}>
                        <td className="payments-td-paid">
                          <input
                            type="checkbox"
                            checked={!!row.paid}
                            onChange={() => handlePaidToggle(row)}
                            aria-label="Mark as paid"
                          />
                        </td>
                        <td>{row.expense_date}</td>
                        <td>{row.category || '—'}</td>
                        <td>{profiles.find((p) => p.id === row.member_id)?.full_name || '—'}</td>
                        <td>{row.title}</td>
                        <td>{row.description || '—'}</td>
                        <td className="payments-td-num">{CURRENCY} {(Number(row.amount) || 0).toFixed(2)}</td>
                        <td className="payments-td-num">
                          {row.category === 'Allowance' ? '—' : `${CURRENCY} ${(Number(row.second_tax) || 0).toFixed(2)}`}
                        </td>
                        <td className="payments-td-num">{CURRENCY} {rowTotal.toFixed(2)}</td>
                        <td className="payments-actions-cell">
                          {isAllowance && row.id ? (
                            <>
                              <button type="button" className="payments-row-btn" title="Edit" onClick={() => openEditModal(row)}>
                                <IconEdit />
                              </button>
                              <button type="button" className="payments-row-btn payments-row-btn-danger" title="Delete" onClick={() => handleDelete(row.id)}>
                                <IconTrash />
                              </button>
                            </>
                          ) : (
                            <span className="payments-cell-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {showAddModal && (
          <div className="estimates-modal-backdrop" onClick={() => setShowAddModal(false)}>
            <div className="estimates-modal" onClick={(e) => e.stopPropagation()}>
              <div className="estimates-modal-header">
                <h2 className="estimates-modal-title">Add allowance</h2>
                <button type="button" className="estimates-modal-close" onClick={() => setShowAddModal(false)} aria-label="Close">×</button>
              </div>
              <form className="estimates-modal-body" onSubmit={handleAddPayment}>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Member</div>
                  <select
                    className="estimates-form-select"
                    value={addForm.member_id}
                    onChange={(e) => setAddForm((f) => ({ ...f, member_id: e.target.value }))}
                  >
                    <option value="">—</option>
                    {profiles.map((p) => (
                      <option key={p.id} value={p.id}>{p.full_name || 'Unnamed'}</option>
                    ))}
                  </select>
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Date</div>
                  <input
                    type="date"
                    className="estimates-form-input"
                    value={addForm.expense_date}
                    onChange={(e) => setAddForm((f) => ({ ...f, expense_date: e.target.value }))}
                  />
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Title</div>
                  <input
                    type="text"
                    className="estimates-form-input"
                    placeholder="e.g. Transport allowance"
                    value={addForm.title}
                    onChange={(e) => setAddForm((f) => ({ ...f, title: e.target.value }))}
                    required
                  />
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Description / note</div>
                  <input
                    type="text"
                    className="estimates-form-input"
                    placeholder="Optional"
                    value={addForm.description}
                    onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Amount ({CURRENCY})</div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="estimates-form-input"
                    value={addForm.amount}
                    onChange={(e) => setAddForm((f) => ({ ...f, amount: e.target.value }))}
                  />
                </div>
                <div className="estimates-modal-footer">
                  <button type="button" className="estimates-footer-btn" onClick={() => setShowAddModal(false)}>Cancel</button>
                  <button type="submit" className="estimates-footer-btn estimates-footer-btn-primary">Add allowance</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditModal && (
          <div className="estimates-modal-backdrop" onClick={closeEditModal}>
            <div className="estimates-modal" onClick={(e) => e.stopPropagation()}>
              <div className="estimates-modal-header">
                <h2 className="estimates-modal-title">Edit allowance</h2>
                <button type="button" className="estimates-modal-close" onClick={closeEditModal} aria-label="Close">×</button>
              </div>
              <form className="estimates-modal-body" onSubmit={handleEditPayment}>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Member</div>
                  <select
                    className="estimates-form-select"
                    value={editForm.member_id}
                    onChange={(e) => setEditForm((f) => ({ ...f, member_id: e.target.value }))}
                  >
                    <option value="">—</option>
                    {profiles.map((p) => (
                      <option key={p.id} value={p.id}>{p.full_name || 'Unnamed'}</option>
                    ))}
                  </select>
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Date</div>
                  <input
                    type="date"
                    className="estimates-form-input"
                    value={editForm.expense_date}
                    onChange={(e) => setEditForm((f) => ({ ...f, expense_date: e.target.value }))}
                  />
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Title</div>
                  <input
                    type="text"
                    className="estimates-form-input"
                    placeholder="Required"
                    value={editForm.title}
                    onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                    required
                  />
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Description / note</div>
                  <input
                    type="text"
                    className="estimates-form-input"
                    placeholder="Optional"
                    value={editForm.description}
                    onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  />
                </div>
                <div className="estimates-form-grid">
                  <div className="estimates-form-label">Amount ({CURRENCY})</div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="estimates-form-input"
                    value={editForm.amount}
                    onChange={(e) => setEditForm((f) => ({ ...f, amount: e.target.value }))}
                  />
                </div>
                <div className="estimates-modal-footer">
                  <button type="button" className="estimates-footer-btn" onClick={closeEditModal}>Cancel</button>
                  <button type="submit" className="estimates-footer-btn estimates-footer-btn-primary">Save allowance</button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
